/**
 * Authenticated Fetch Utility
 * 
 * This module provides a fetch wrapper that includes the Firebase ID token
 * in the Authorization header for Safari compatibility.
 * 
 * Safari's ITP blocks third-party cookies, so we need to send the auth token
 * in the header for cross-origin requests to work properly.
 */

import { getAuthToken } from "./authToken";

export interface AuthenticatedFetchOptions extends RequestInit {
    // All standard fetch options are supported
}

/**
 * Fetch with authentication - automatically includes Firebase ID token
 * in the Authorization header for Safari compatibility.
 * 
 * Usage:
 *   const res = await authenticatedFetch('/api/some-endpoint', {
 *     method: 'POST',
 *     body: JSON.stringify(data),
 *   });
 */
export async function authenticatedFetch(
    url: string,
    options: AuthenticatedFetchOptions = {}
): Promise<Response> {
    // Build headers with auth token
    const headers = new Headers(options.headers);

    // Always include credentials for session cookie fallback
    const fetchOptions: RequestInit = {
        ...options,
        credentials: 'include',
        headers,
    };

    // Try to get and add the Firebase ID token
    try {
        const token = await getAuthToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
    } catch (error) {
        console.warn('[authenticatedFetch] Failed to get auth token:', error);
        // Continue without token - session cookie may still work
    }

    // Prepend API base URL if it's a relative path
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;

    return fetch(fullUrl, fetchOptions);
}

/**
 * Convenience method for authenticated JSON POST requests
 */
export async function authenticatedPost(
    url: string,
    data?: unknown
): Promise<Response> {
    return authenticatedFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
    });
}

/**
 * Convenience method for authenticated JSON PUT requests
 */
export async function authenticatedPut(
    url: string,
    data?: unknown
): Promise<Response> {
    return authenticatedFetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
    });
}

/**
 * Convenience method for authenticated DELETE requests
 */
export async function authenticatedDelete(url: string): Promise<Response> {
    return authenticatedFetch(url, {
        method: 'DELETE',
    });
}
